import type { z } from 'zod';

import type { CashMovementDto, CashVarianceActionDto, CashVarianceCaseDto } from '../cash/index.js';
import type { CashTargetStateSyncPayloadDtoSchema } from './cash-target-state-sync.schema.js';
import type { CashSyncOperationEnvelopeDto } from './cash-sync.dto.js';

export const CASH_VARIANCE_OPERATION_ENTITY_TYPES = {
  recoverySubmitted: 'cashVarianceRecoverySubmission',
  recoveryApproved: 'cashVarianceRecoveryApproval',
  recoveryRejected: 'cashVarianceRecoveryRejection',
  writeoffApplied: 'cashVarianceWriteoff',
  offsetApplied: 'cashVarianceOffset',
  overageClassified: 'cashVarianceOverageClassification',
  overageRemoved: 'cashVarianceOverageRemoval',
} as const;

export const cashVarianceOperationSyncEventTypeValues = [
  'cashVarianceRecognized',
  'cashVarianceRecoverySubmitted',
  'cashVarianceRecoveryApproved',
  'cashVarianceRecoveryRejected',
  'cashVarianceWriteoffApplied',
  'cashVarianceOffsetApplied',
  'cashVarianceOverageClassified',
  'cashVarianceOverageRemoved',
] as const;
export type CashVarianceOperationSyncEventType =
  (typeof cashVarianceOperationSyncEventTypeValues)[number];

export type CashVarianceOperationResultEntityType =
  | 'cashSessionOpen'
  | 'cashSessionClose'
  | 'cashSessionForceClose'
  | (typeof CASH_VARIANCE_OPERATION_ENTITY_TYPES)[keyof typeof CASH_VARIANCE_OPERATION_ENTITY_TYPES];

export const cashTargetStateSyncEntityTypeValues = [
  'cashModeActivation',
  'cashScopeModeState',
  'cashNeededRequest',
  'cashLocalTransfer',
  'cashLocalTransferCorrection',
  'cashExpenseMetadataCorrection',
  'cashDenominationCorrection',
  'cashTreasuryDistributionRequest',
  'cashCustodyReceipt',
  'cashVaultRemittance',
  'cashVarianceOperation',
] as const;
export type CashTargetStateSyncEntityType = (typeof cashTargetStateSyncEntityTypeValues)[number];

export const cashTargetStateSyncEventTypeValues = [
  'cashModeActivationStarted',
  'cashModeActivationReady',
  'cashModeActivationCompleted',
  'cashModeActivationCancelled',
  'cashScopeModeStateChanged',
  'cashNeededRequestOpened',
  'cashNeededRequestFulfilled',
  'cashNeededRequestCancelled',
  'cashLocalTransferApplied',
  'cashLocalTransferReversed',
  'cashLocalTransferReplaced',
  'cashLocalTransferCorrected',
  'cashExpenseMetadataCorrected',
  'cashExpenseReplaced',
  'cashDenominationsCorrected',
  'cashTreasuryDistributionRequestDeclined',
  'cashCustodyReceiptRecorded',
  'cashCustodyReceiptReversed',
  'cashCustodyReceiptReconciled',
  'cashVaultRemittanceCreated',
  'cashVaultRemittanceUpdated',
  'cashVaultRemittancePrepared',
  'cashVaultRemittanceHandedOff',
  'cashVaultRemittanceConfirmed',
  'cashVaultRemittanceCancelled',
  'cashVaultRemittanceReturned',
  'cashVaultRemittanceExcepted',
  'cashVaultRemittanceVoided',
  ...cashVarianceOperationSyncEventTypeValues,
] as const;
export type CashTargetStateSyncEventType = (typeof cashTargetStateSyncEventTypeValues)[number];

export interface CashTargetStateSyncMutationIdentityDto {
  operationId: string;
  requestHash: string;
  companyId: string;
  scopeType: 'location' | 'treasury';
  scopeId: string;
  actorUserId: string;
  occurredAt: string;
}

export type CashVarianceOperationSyncDataDto = {
  operation: CashSyncOperationEnvelopeDto;
  cases: CashVarianceCaseDto[];
  actions: CashVarianceActionDto[];
  movements: CashMovementDto[];
};

/** Strict event-discriminated target-state result graph produced by the runtime schema. */
export type CashTargetStateSyncPayloadDto = z.output<typeof CashTargetStateSyncPayloadDtoSchema>;

export interface CashVarianceOperationSyncPayloadDto
  extends CashTargetStateSyncMutationIdentityDto {
  id: string;
  entityType: 'cashVarianceOperation';
  eventType: CashVarianceOperationSyncEventType;
  resultEntityType: CashVarianceOperationResultEntityType;
  data: CashVarianceOperationSyncDataDto;
}
